/**
 * Angular service for Comments API operations
 * 
 * @remarks
 * Provides comprehensive methods for managing blog post comments including:
 * - Comment creation and updates
 * - Hierarchical comment retrieval (with nested replies)
 * - Reaction management (like, dislike, love, laugh, angry, sad)
 * - Comment deletion
 * 
 * All methods return RxJS Observables for reactive programming patterns.
 * Authentication is handled by HTTP interceptors in the consuming application.
 * 
 * @example
 * ```typescript
 * // In your module or standalone component
 * import { provideCommentsService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideCommentsService({
 *       apiBaseUrl: environment.apiBaseUrl
 *     })
 *   ]
 * };
 * 
 * // In your component
 * constructor(private commentsService: CommentsService) {}
 * 
 * ngOnInit() {
 *   this.commentsService.getComments({ blogPostId: '507f1f77bcf86cd799439011' })
 *     .subscribe(response => {
 *       this.comments = response.data;
 *     });
 * }
 * ```
 * 
 * @see COMMENTS_API_REFERENCE.md for complete API documentation
 */

import { Injectable, inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  CommentContentType,
  CommentPopulated,
  CreateCommentDto,
  UpdateCommentDto,
  AddReactionDto,
  GetCommentsQueryParams,
  CreateCommentResponse,
  UpdateCommentResponse,
  DeleteCommentResponse,
  AddReactionResponse,
  RemoveReactionResponse,
  CommentsListResponse,
  CommentResponse
} from '../models/comment.model';

/**
 * Configuration interface for Comments service
 */
export interface CommentsConfig {
  /**
   * Base URL for the API (without trailing slash)
   * @example 'https://api.example.com'
   */
  apiBaseUrl: string;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Injection token for Comments service configuration
 */
export const COMMENTS_API_CONFIG = new InjectionToken<CommentsConfig>(
  'COMMENTS_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => ({
      apiBaseUrl: '',
      debug: false
    })
  }
);

/**
 * Comments API Service
 * 
 * @remarks
 * Manages all comment-related operations for blog posts.
 * Supports hierarchical comments (replies), reactions, and nested reply loading.
 */
@Injectable({
  providedIn: 'root'
})
export class CommentsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(COMMENTS_API_CONFIG);
  private readonly baseUrl = `${this.config.apiBaseUrl}/api/comments`;

  /**
   * Log debug messages if debug mode is enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[CommentsService] ${message}`, ...args);
    }
  }

  /**
   * Create a new comment
   * 
   * @remarks
   * Creates either a top-level comment or a reply to an existing comment.
   * The current user (from auth token) is set as the author.
   * 
   * @param dto - Comment creation data
   * @returns Observable of created comment with populated references
   * 
   * @example
   * ```typescript
   * // Create top-level comment
   * const newComment: CreateCommentDto = {
   *   blogPostId: '507f1f77bcf86cd799439011',
   *   comment: 'Great article!'
   * };
   * 
   * this.commentsService.createComment(newComment).subscribe(
   *   response => console.log('Comment created:', response.data)
   * );
   * 
   * // Create reply
   * const reply: CreateCommentDto = {
   *   blogPostId: '507f1f77bcf86cd799439011',
   *   comment: 'I agree!',
   *   parentCommentId: '507f191e810c19729de860ea'
   * };
   * 
   * this.commentsService.createComment(reply).subscribe(
   *   response => console.log('Reply created:', response.data)
   * );
   * ```
   */
  createComment(dto: CreateCommentDto): Observable<CreateCommentResponse> {
    this.log('Creating comment', dto);
    return this.http.post<CreateCommentResponse>(this.baseUrl, dto).pipe(
      tap(response => this.log('Comment created successfully', response.data))
    );
  }

  /**
   * Get comments for a blog post
   * 
   * @remarks
   * Retrieves comments with optional filtering and nested reply loading.
   * Supports pagination and configurable reply limits.
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of comment list with pagination metadata
   * 
   * @example
   * ```typescript
   * // Get all top-level comments with nested replies
   * this.commentsService.getComments({
   *   blogPostId: '507f1f77bcf86cd799439011',
   *   parentCommentId: 'null', // Top-level only
   *   includeReplies: true,
   *   repliesLimit: 5,
   *   page: 1,
   *   limit: 20
   * }).subscribe(response => {
   *   console.log('Comments:', response.data);
   *   console.log('Total pages:', response.pagination.pages);
   * });
   * 
   * // Get all comments (top-level + all replies)
   * this.commentsService.getComments({
   *   blogPostId: '507f1f77bcf86cd799439011'
   * }).subscribe(response => {
   *   console.log('All comments:', response.data);
   * });
   * ```
   */
  getComments(params: GetCommentsQueryParams): Observable<CommentsListResponse> {
    this.log('Getting comments', params);
    const httpParams = this._buildCommentsParams(params);
    return this.http.get<CommentsListResponse>(this.baseUrl, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.count} comments`, response.pagination))
    );
  }

  /**
   * Get comments for a publicly published blog post (no authentication required)
   *
   * @remarks
   * Calls GET /api/comments/public — publicly accessible, no Bearer token needed.
   * Author emails are excluded from the response.
   * Results are cached server-side (default TTL: 5 minutes).
   * Intended for use in public-facing reader apps (e.g. stratum-reader).
   *
   * @param params - Same query parameters as {@link getComments}
   * @returns Observable of comment list with pagination metadata
   *
   * @example
   * ```typescript
   * this.commentsService.getPublicComments({
   *   blogPostId: '507f1f77bcf86cd799439011',
   *   includeReplies: true,
   *   repliesLimit: 5
   * }).subscribe(response => {
   *   console.log('Public comments:', response.data);
   * });
   * ```
   */
  getPublicComments(params: GetCommentsQueryParams): Observable<CommentsListResponse> {
    this.log('Getting public comments', params);
    const httpParams = this._buildCommentsParams(params);
    return this.http.get<CommentsListResponse>(`${this.baseUrl}/public`, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.count} public comments`, response.pagination))
    );
  }

  /** Shared param-builder used by both getComments and getPublicComments */
  private _buildCommentsParams(params: GetCommentsQueryParams): HttpParams {
    let httpParams = new HttpParams()
      .set('contentType', params.contentType)
      .set('contentId', params.contentId);

    if (params.parentCommentId !== undefined) {
      httpParams = httpParams.set('parentCommentId', params.parentCommentId);
    }
    if (params.includeReplies !== undefined) {
      httpParams = httpParams.set('includeReplies', params.includeReplies.toString());
    }
    if (params.repliesLimit !== undefined) {
      httpParams = httpParams.set('repliesLimit', params.repliesLimit.toString());
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    return httpParams;
  }

  /**
   * Update a comment
   * 
   * @remarks
   * Only the comment text can be updated.
   * User must be the comment author.
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @param dto - Updated comment data
   * @returns Observable of updated comment with populated references
   * 
   * @example
   * ```typescript
   * const updates: UpdateCommentDto = {
   *   comment: 'Updated comment text'
   * };
   * 
   * this.commentsService.updateComment('507f191e810c19729de860ea', updates)
   *   .subscribe(response => {
   *     console.log('Comment updated:', response.data);
   *   });
   * ```
   */
  updateComment(commentId: string, dto: UpdateCommentDto): Observable<UpdateCommentResponse> {
    this.log('Updating comment', commentId, dto);
    return this.http.put<UpdateCommentResponse>(`${this.baseUrl}/${commentId}`, dto).pipe(
      tap(response => this.log('Comment updated successfully', response.data))
    );
  }

  /**
   * Delete a comment
   * 
   * @remarks
   * Soft deletes the comment (sets isActive: false).
   * User must be the comment author.
   * When a parent comment is deleted, its replies remain but show as orphaned.
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of deletion confirmation
   * 
   * @example
   * ```typescript
   * this.commentsService.deleteComment('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log(response.message); // "Comment deleted successfully"
   *   });
   * ```
   */
  deleteComment(commentId: string): Observable<DeleteCommentResponse> {
    this.log('Deleting comment', commentId);
    return this.http.delete<DeleteCommentResponse>(`${this.baseUrl}/${commentId}`).pipe(
      tap(response => this.log('Comment deleted successfully', response.message))
    );
  }

  /**
   * Add a reaction to a comment
   * 
   * @remarks
   * Adds an emoji reaction (like, dislike, love, laugh, angry, sad).
   * Users can add multiple reactions to the same comment.
   * The current user (from auth token) is set as the reaction author.
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @param dto - Reaction data
   * @returns Observable of updated comment with new reaction
   * 
   * @example
   * ```typescript
   * const reaction: AddReactionDto = {
   *   type: 'love'
   * };
   * 
   * this.commentsService.addReaction('507f191e810c19729de860ea', reaction)
   *   .subscribe(response => {
   *     console.log('Reaction added:', response.data.reactions);
   *   });
   * ```
   */
  addReaction(commentId: string, dto: AddReactionDto): Observable<AddReactionResponse> {
    this.log('Adding reaction to comment', commentId, dto);
    return this.http.post<AddReactionResponse>(`${this.baseUrl}/${commentId}/reactions`, dto).pipe(
      tap(response => this.log('Reaction added successfully', response.data))
    );
  }

  /**
   * Remove a user's reaction from a comment
   * 
   * @remarks
   * Removes all reactions of the current user from the specified comment.
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment without user's reactions
   * 
   * @example
   * ```typescript
   * this.commentsService.removeReaction('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log('Reaction removed:', response.data.reactions);
   *   });
   * ```
   */
  removeReaction(commentId: string): Observable<RemoveReactionResponse> {
    this.log('Removing reaction from comment', commentId);
    return this.http.delete<RemoveReactionResponse>(`${this.baseUrl}/${commentId}/reactions`).pipe(
      tap(response => this.log('Reaction removed successfully', response.data))
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get top-level comments only (no replies)
   * 
   * @param blogPostId - MongoDB ObjectId of the blog post
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Observable of top-level comments
   * 
   * @example
   * ```typescript
   * this.commentsService.getTopLevelComments('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Top-level comments:', response.data);
   *   });
   * ```
   */
  getTopLevelComments(contentType: CommentContentType, contentId: string, page = 1, limit = 20): Observable<CommentsListResponse> {
    this.log('Getting top-level comments', contentType, contentId);
    return this.getComments({
      contentType,
      contentId,
      parentCommentId: 'null',
      includeReplies: false,
      page,
      limit
    });
  }

  /**
   * Get replies for a specific comment
   * 
   * @param blogPostId - MongoDB ObjectId of the blog post
   * @param parentCommentId - MongoDB ObjectId of the parent comment
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Observable of reply comments
   * 
   * @example
   * ```typescript
   * this.commentsService.getReplies(
   *   '507f1f77bcf86cd799439011',
   *   '507f191e810c19729de860ea'
   * ).subscribe(response => {
   *   console.log('Replies:', response.data);
   * });
   * ```
   */
  getReplies(contentType: CommentContentType, contentId: string, parentCommentId: string, page = 1, limit = 20): Observable<CommentsListResponse> {
    this.log('Getting replies for comment', parentCommentId);
    return this.getComments({
      contentType,
      contentId,
      parentCommentId,
      includeReplies: false,
      page,
      limit
    });
  }

  /**
   * Get top-level comments with nested replies
   * 
   * @param blogPostId - MongoDB ObjectId of the blog post
   * @param repliesLimit - Maximum number of replies per comment (default: 5)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Observable of comments with nested replies
   * 
   * @example
   * ```typescript
   * this.commentsService.getCommentsWithReplies('507f1f77bcf86cd799439011', 10)
   *   .subscribe(response => {
   *     response.data.forEach(comment => {
   *       console.log('Comment:', comment.comment);
   *       console.log('Replies:', comment.replies);
   *       console.log('Has more:', comment.hasMoreReplies);
   *     });
   *   });
   * ```
   */
  getCommentsWithReplies(
    contentType: CommentContentType,
    contentId: string,
    repliesLimit = 5,
    page = 1,
    limit = 20
  ): Observable<CommentsListResponse> {
    this.log('Getting comments with nested replies', contentType, contentId);
    return this.getComments({
      contentType,
      contentId,
      parentCommentId: 'null',
      includeReplies: true,
      repliesLimit,
      page,
      limit
    });
  }

  /**
   * Create a reply to an existing comment
   * 
   * @param blogPostId - MongoDB ObjectId of the blog post
   * @param parentCommentId - MongoDB ObjectId of the parent comment
   * @param comment - Reply text (1-1000 characters)
   * @returns Observable of created reply
   * 
   * @example
   * ```typescript
   * this.commentsService.createReply(
   *   '507f1f77bcf86cd799439011',
   *   '507f191e810c19729de860ea',
   *   'Thanks for sharing!'
   * ).subscribe(response => {
   *   console.log('Reply created:', response.data);
   * });
   * ```
   */
  createReply(contentType: CommentContentType, contentId: string, parentCommentId: string, comment: string): Observable<CreateCommentResponse> {
    this.log('Creating reply', { contentType, contentId, parentCommentId, comment });
    return this.createComment({
      contentType,
      contentId,
      comment,
      parentCommentId
    });
  }

  /**
   * Add a "like" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  likeComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'like' });
  }

  /**
   * Add a "dislike" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  dislikeComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'dislike' });
  }

  /**
   * Add a "love" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  loveComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'love' });
  }

  /**
   * Add a "laugh" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  laughAtComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'laugh' });
  }

  /**
   * Add an "angry" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  angryAtComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'angry' });
  }

  /**
   * Add a "sad" reaction to a comment
   * 
   * @param commentId - MongoDB ObjectId of the comment
   * @returns Observable of updated comment
   */
  sadAtComment(commentId: string): Observable<AddReactionResponse> {
    return this.addReaction(commentId, { type: 'sad' });
  }
}

/**
 * Provider function for Comments service
 * 
 * @remarks
 * Use this function to configure the CommentsService in your application.
 * 
 * @param config - Comments service configuration
 * @returns Provider configuration
 * 
 * @example
 * ```typescript
 * import { provideCommentsService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideCommentsService({
 *       apiBaseUrl: environment.apiBaseUrl,
 *       debug: !environment.production
 *     })
 *   ]
 * };
 * ```
 */
export function provideCommentsService(config: CommentsConfig) {
  return {
    provide: COMMENTS_API_CONFIG,
    useValue: config
  };
}
